import React, { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getData, postData } from '../services/api';
import { useDemo } from '../context/DemoContext';
import { playNotifSound, unlockAudio } from '../utils/sounds';
import { showBrowserNotification } from '../utils/pushNotifications';
import VersionBadge from '../components/common/VersionBadge';
import { isCollegeStudentExperience } from '../utils/studentExperience';

const CATEGORY_LABELS = {
  administration: 'Administration',
  academics: 'Academics',
  learning: 'Learning',
  hrm: 'HR & People',
  finance: 'Finance',
  crm: 'CRM & Sales',
  operations: 'Operations',
  communication: 'Communication',
  reports: 'Reports',
};

const CATEGORY_ORDER = [
  'administration', 'academics', 'learning', 'finance', 'hrm', 'crm', 'operations', 'communication', 'reports',
];

const normalizeBootstrapIcon = (icon) => {
  const raw = String(icon || '').trim();
  if (!raw) return 'bi bi-grid';
  if (raw.startsWith('bi ')) return raw;
  if (raw.startsWith('bi-')) return `bi ${raw}`;
  return `bi bi-${raw}`;
};

const normalizeTenantAssetUrl = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const token = localStorage.getItem('token');
  const appendToken = (url) => {
    if (!token || /[?&]t=/.test(url)) return url;
    return url.includes('?') ? `${url}&t=${encodeURIComponent(token)}` : `${url}?t=${encodeURIComponent(token)}`;
  };
  if (/^(data:|blob:)/i.test(raw)) return raw;
  if (/^https?:/i.test(raw)) {
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const backendOrigin = apiBase
        ? apiBase.replace(/\/api\/?$/, '')
        : (typeof window !== 'undefined' ? window.location.origin : '');
      const parsed = new URL(raw);
      if (backendOrigin) return appendToken(`${backendOrigin}${parsed.pathname}${parsed.search || ''}`);
    } catch (_) {}
    return appendToken(raw);
  }
  return appendToken(raw);
};


// Hardcoded menuAccess per demo role key â€” mirrors the seed-demo.js role menus
// Fallback demo menus â€” only used when DB role has no menuAccess for that key.
// DB role menuAccess is always preferred (it's industry-specific). These are generic fallbacks.
const DEMO_ROLE_MENUS = {
  super_admin:   null,  // null = show all
  admin:         null,
  manager:       null,
  team_lead:     null,
  staff:         ['dashboard','tasks','leave','work-attendance','chat','feedback','announcements'],
  viewer:        ['dashboard','chat'],
  // training institute trainer/trainee
  faculty:       ['dashboard','attendance','assessments','tasks','course-content','timetable','leave','work-attendance','feedback'],
  teacher:       ['dashboard','attendance','assessments','tasks','course-content','timetable','leave','work-attendance','feedback',
                  // education keys (used when DB role not found)
                  'edu_attendance','edu_assessments','edu_timetable','advanced_course_content','student_course','syllabus_tracking'],
  student:       ['dashboard','attendance','assessments','course-content','resumes','feedback','online_learning',
                  'edu_attendance','edu_assessments','edu_timetable','student_course','student_performance'],
  parent:        ['dashboard','attendance','course-content','assessments','timetable','feedback',
                  'edu_attendance','student_course','edu_assessments','edu_timetable','parent_communication'],
  // hospital
  doctor:        ['dashboard','patient_management','appointment_management','opd_management','medical_records','tasks','chat','leave','work-attendance','feedback'],
  nurse:         ['dashboard','patient_management','appointment_management','tasks','chat','leave','work-attendance','feedback'],
  patient:       ['dashboard','feedback'],
  // software
  developer:     ['dashboard','project_management','sprint_management','bug_tracker','tasks','timesheet','knowledge_base','chat','leave','work-attendance','feedback'],
  tester:        ['dashboard','bug_tracker','tasks','timesheet','chat','leave','feedback'],
  // retail
  cashier:       ['dashboard','pos','tasks','chat','feedback'],
  store_manager: ['dashboard','pos','inventory_management','sales_management','customer_management','delivery_management','tasks','chat','leave','work-attendance','feedback'],
};

export default function TenantLayout() {
  const { user, tenant, activeModules, role, logout } = useAuth();
  const { demoMode, getEffectiveUser, demoTenantModules, demoTenantRoles, demoTenantName } = useDemo();
  const initialRoleKey = String(user?.roleKey || user?.role || '').toLowerCase();
  const initialStudentShell = initialRoleKey === 'student' && isCollegeStudentExperience(user);
  const [collapsed, setCollapsed] = useState(() => initialStudentShell);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pendingUsersCount, setPendingUsersCount] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [liveToast, setLiveToast] = useState(null);
  const [settingsLogo, setSettingsLogo] = useState('');
  const [studentMoreOpen, setStudentMoreOpen] = useState(false);
  const [studentHasAssessmentContent, setStudentHasAssessmentContent] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() => window.innerWidth <= 991);
  const toastTimerRef = useRef(null);
  const dynamicManifestUrlRef = useRef(null);
  const location = useLocation();
  const overlayRef = useRef(null);

  useEffect(() => {
    if (initialStudentShell) {
      setCollapsed(true);
    }
  }, [initialStudentShell]);

  useEffect(() => {
    if (!user || user?.userType === 'super_admin') return;
    let alive = true;
    getData('/settings')
      .then((res) => {
        if (!alive) return;
        const data = res?.data || res || {};
        const nextLogo = data?.branding?.logo
          || data?.branding?.logoUrl
          || data?.logo
          || data?.logoUrl
          || '';
        setSettingsLogo(nextLogo);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [user]);

  useEffect(() => {
    const handleResize = () => setIsMobileViewport(window.innerWidth <= 991);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Unlock Web Audio API on first user interaction (browser autoplay policy).
  useEffect(() => {
    document.addEventListener('click', unlockAudio, { once: true });
  }, []);

  // Poll pending-user count so tenant admins see the badge without manual
  // refresh. Only admin / manager / super_admin can read /users-pending â€”
  // calling it from any other role produces a 403 every minute, which spams
  // the console and the server logs. Skip the poll entirely for non-elevated
  // roles instead of relying on a swallowed error.
  const pendingRoleKey = user?.roleKey || user?.role || '';
  const canSeePendingUsers =
    user?.userType === 'super_admin' ||
    ['admin', 'manager'].includes(pendingRoleKey);

  useEffect(() => {
    if (!user || !canSeePendingUsers) { setPendingUsersCount(0); return; }
    let cancelled = false;
    const fetchCount = () => {
      getData('/users-pending')
        .then(res => { if (!cancelled) setPendingUsersCount(Array.isArray(res?.data) ? res.data.length : 0); })
        .catch(() => { if (!cancelled) setPendingUsersCount(0); });
    };
    fetchCount();
    const id = setInterval(fetchCount, 60000); // refresh every minute
    return () => { cancelled = true; clearInterval(id); };
  }, [user, canSeePendingUsers]);

  // Also refresh pending count whenever the user navigates to /users (so approvals reflect immediately).
  useEffect(() => {
    if (!user || !canSeePendingUsers) return;
    if (location.pathname === '/users') {
      getData('/users-pending')
        .then(res => setPendingUsersCount(Array.isArray(res?.data) ? res.data.length : 0))
        .catch(() => {});
    }
  }, [location.pathname, user, canSeePendingUsers]);

  // Real-time notifications via SSE. Opens one long-lived EventSource per
  // session, reconnects with exponential backoff if the server drops it.
  // Server sends { type:'count' } on connect and { type:'notification', notification }
  // per event. ERR_CONNECTION_RESET is expected during backend restarts
  // (nodemon) â€” the reconnect loop handles it silently.
  useEffect(() => {
    if (!user || user.userType === 'super_admin') { setUnreadNotifications(0); return; }
    const token = localStorage.getItem('token');
    if (!token) return;

    const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/$/, '');
    let es = null;
    let retry = null;
    let closed = false;
    let attempts = 0;

    const connect = () => {
      if (closed) return;
      try {
        es = new EventSource(`${apiBase}/shared/notifications/stream?token=${encodeURIComponent(token)}`);
        es.onopen = () => { attempts = 0; }; // reset backoff on a good connection
        es.onmessage = (ev) => {
          try {
            const data = JSON.parse(ev.data);
            if (data.type === 'count') {
              setUnreadNotifications(data.count || 0);
            } else if (data.type === 'notification') {
              setUnreadNotifications(c => c + 1);
              const n = data.notification;
              if (n) {
                setLiveToast(n);
                playNotifSound().catch(() => {});
                if (document.visibilityState !== 'visible') {
                  showBrowserNotification({
                    title: n.title || 'TSLMS Notification',
                    body: n.message || '',
                    link: n.link || '/notifications',
                    tag: n._id ? `tenant-notification:${n._id}` : `tenant-notification:${Date.now()}`,
                  }).catch(() => {});
                }
                if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
                toastTimerRef.current = setTimeout(() => setLiveToast(null), 6000);
              }
            }
          } catch (_) { /* ignore heartbeats / malformed frames */ }
        };
        es.onerror = () => {
          try { es.close(); } catch (_) {}
          if (closed) return;
          // Exponential backoff: 2s, 4s, 8s, 16s, capped at 30s.
          attempts = Math.min(attempts + 1, 5);
          const delay = Math.min(1000 * Math.pow(2, attempts), 30000);
          retry = setTimeout(connect, delay);
        };
      } catch (_) { retry = setTimeout(connect, 5000); }
    };
    connect();

    return () => {
      closed = true;
      if (retry) clearTimeout(retry);
      if (es) { try { es.close(); } catch (_) {} }
    };
  }, [user]);

  // When the user visits /notifications, mark the count as read (badge clears).
  // The notifications page itself marks individual records read on the server.
  useEffect(() => {
    if (location.pathname === '/notifications') setUnreadNotifications(0);
  }, [location.pathname]);

    // Keep browser tab title and mobile app labels aligned to the tenant name.
  useEffect(() => {
    const companyName = tenant?.name || tenant?.companyName;
    document.title = companyName || 'TOP SKILLED ACADEMY';
  }, [tenant?.name, tenant?.companyName, location.pathname]);

  useEffect(() => {
    const appName = tenant?.name || tenant?.companyName || 'TOP SKILLED ACADEMY';
    [
      ['name', 'application-name'],
      ['name', 'apple-mobile-web-app-title'],
    ].forEach(([attr, value]) => {
      let meta = document.querySelector(`meta[${attr}="${value}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, value);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', appName);
    });
    let description = document.querySelector('meta[name="description"]');
    if (!description) {
      description = document.createElement('meta');
      description.setAttribute('name', 'description');
      document.head.appendChild(description);
    }
    description.setAttribute('content', `${appName} learning and operations platform`);
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) {
      const manifestPayload = {
        short_name: appName.length > 12 ? appName.slice(0, 12) : appName,
        name: appName,
        description: `${appName} learning and operations platform`,
        icons: [
          { src: `${window.location.origin}/favicon.png`, sizes: '192x192', type: 'image/png' },
          { src: `${window.location.origin}/favicon.png`, sizes: '512x512', type: 'image/png' },
        ],
        start_url: '.',
        display: 'standalone',
        theme_color: tenant?.branding?.primaryColor || '#2563eb',
        background_color: '#ffffff',
      };
      const nextUrl = URL.createObjectURL(new Blob([JSON.stringify(manifestPayload)], { type: 'application/manifest+json' }));
      manifestLink.setAttribute('href', nextUrl);
      if (dynamicManifestUrlRef.current) URL.revokeObjectURL(dynamicManifestUrlRef.current);
      dynamicManifestUrlRef.current = nextUrl;
    }
    return () => {
      if (dynamicManifestUrlRef.current) {
        URL.revokeObjectURL(dynamicManifestUrlRef.current);
        dynamicManifestUrlRef.current = null;
      }
    };
  }, [tenant?.name, tenant?.companyName, tenant?.branding?.primaryColor]);

  // Keep tenant_industry_slug in localStorage fresh for other components
  useEffect(() => {
    if (!user) return;
    getData('/training/rbac/my-access')
      .then(res => {
        if (res.success && res.data?.industry) {
          localStorage.setItem('tenant_industry_slug', res.data.industry.toLowerCase());
        }
      })
      .catch(() => {});
  }, [user?.id, user?._id, user?.tenantId]); // eslint-disable-line

  // Apply tenant branding (CSS variables used across the whole app)
  useEffect(() => {
    // Prefer API data; fall back to localStorage cached branding for instant load
    const b = (tenant?.branding && Object.keys(tenant.branding).length > 0)
      ? tenant.branding
      : JSON.parse(localStorage.getItem('tenant_branding') || '{}');

    const root = document.documentElement;
    const setColor = (varName, hex) => {
      if (!hex || !hex.startsWith('#') || hex.length < 7) return;
      root.style.setProperty(varName, hex);
      try {
        const r  = parseInt(hex.slice(1, 3), 16);
        const g  = parseInt(hex.slice(3, 5), 16);
        const bv = parseInt(hex.slice(5, 7), 16);
        root.style.setProperty(`${varName}-rgb`, `${r},${g},${bv}`);
        const dk = v => Math.max(0, v - 35).toString(16).padStart(2, '0');
        root.style.setProperty(`${varName}-dark`,  `#${dk(r)}${dk(g)}${dk(bv)}`);
        const lt = v => Math.min(255, v + 40).toString(16).padStart(2, '0');
        root.style.setProperty(`${varName}-light`, `#${lt(r)}${lt(g)}${lt(bv)}`);
      } catch (_) {}
    };
    setColor('--tenant-primary',   b.primaryColor);
    setColor('--tenant-secondary', b.secondaryColor);
    setColor('--tenant-accent',    b.accentColor);

    // Extend overrides so the brand colour actually shows up on Bootstrap
    // components. Setting `--bs-primary` alone is not enough â€” BS5 buttons,
    // pills, badges, progress bars read per-component variables. We inject a
    // <style id="tenant-theme"> block with the concrete selectors so the brand
    // colour applies site-wide without per-page changes.
    if (b.primaryColor) {
      const hex = b.primaryColor;
      root.style.setProperty('--bs-primary', hex);
      root.style.setProperty('--bs-link-color', hex);

      const r  = parseInt(hex.slice(1, 3), 16);
      const g  = parseInt(hex.slice(3, 5), 16);
      const bv = parseInt(hex.slice(5, 7), 16);
      const rgb = `${r},${g},${bv}`;
      const darken = (amt) => {
        const f = (v) => Math.max(0, Math.round(v - amt)).toString(16).padStart(2, '0');
        return `#${f(r)}${f(g)}${f(bv)}`;
      };
      const primaryDark = darken(25);

      root.style.setProperty('--bs-primary-rgb', rgb);
      root.style.setProperty('--bs-link-hover-color', primaryDark);

      // Sidebar background follows the brand primary colour (with a darker
      // variant for the hover/active state). The Settings preview paints it
      // this way too â€” users expect the real sidebar to match.
      //
      // Text on a coloured sidebar needs to be white (not the default slate
      // grey) for contrast. Override the text and section-label variables
      // plus inject explicit rules since some of them are set with !important
      // or resolved via the CSS var inline.
      const sidebarDeep = darken(60);   // darkest variant for hover/active
      root.style.setProperty('--sidebar-bg', hex);
      root.style.setProperty('--sidebar-active-bg', `rgba(0, 0, 0, 0.25)`);
      root.style.setProperty('--sidebar-hover-bg', `rgba(255, 255, 255, 0.15)`);
      root.style.setProperty('--sidebar-text', 'rgba(255,255,255,0.92)');
      root.style.setProperty('--sidebar-active-text', '#fff');
      root.style.setProperty('--primary', hex);
      root.style.setProperty('--primary-dark', sidebarDeep);
      root.style.setProperty('--primary-light', `rgba(${rgb}, 0.12)`);

      const css = `
        /* Buttons */
        .btn-primary {
          --bs-btn-bg: ${hex};
          --bs-btn-border-color: ${hex};
          --bs-btn-hover-bg: ${primaryDark};
          --bs-btn-hover-border-color: ${primaryDark};
          --bs-btn-active-bg: ${primaryDark};
          --bs-btn-active-border-color: ${primaryDark};
          --bs-btn-disabled-bg: ${hex};
          --bs-btn-disabled-border-color: ${hex};
        }
        .btn-outline-primary {
          --bs-btn-color: ${hex};
          --bs-btn-border-color: ${hex};
          --bs-btn-hover-bg: ${hex};
          --bs-btn-hover-border-color: ${hex};
          --bs-btn-active-bg: ${hex};
          --bs-btn-active-border-color: ${hex};
          --bs-btn-disabled-color: ${hex};
          --bs-btn-disabled-border-color: ${hex};
        }
        /* Backgrounds / text utilities */
        .bg-primary { background-color: ${hex} !important; }
        .text-primary { color: ${hex} !important; }
        .border-primary { border-color: ${hex} !important; }
        .bg-primary-subtle { background-color: rgba(${rgb}, 0.12) !important; }
        .text-primary-emphasis { color: ${primaryDark} !important; }
        .border-primary-subtle { border-color: rgba(${rgb}, 0.35) !important; }
        /* Nav / tabs */
        .nav-pills .nav-link.active,
        .nav-pills .show > .nav-link { background-color: ${hex} !important; color: #fff !important; }
        .nav-tabs .nav-link.active { color: ${hex} !important; border-bottom-color: ${hex} !important; }
        .nav-link { color: ${hex}; }
        /* Badges */
        .badge.bg-primary { background-color: ${hex} !important; }
        /* Progress */
        .progress-bar { background-color: ${hex}; }
        /* Links */
        a { color: ${hex}; }
        a:hover { color: ${primaryDark}; }
        /* Form controls */
        .form-check-input:checked { background-color: ${hex}; border-color: ${hex}; }
        .form-control:focus, .form-select:focus {
          border-color: rgba(${rgb}, 0.45);
          box-shadow: 0 0 0 0.25rem rgba(${rgb}, 0.2);
        }
        /* Spinners, pagination */
        .spinner-border.text-primary { color: ${hex} !important; }
        .page-link { color: ${hex}; }
        .page-item.active .page-link { background-color: ${hex}; border-color: ${hex}; }
        /* Dropdown active item */
        .dropdown-item.active, .dropdown-item:active { background-color: ${hex}; }
        /* Sidebar text â€” force white for contrast on any brand colour */
        .app-sidebar .nav-link-item { color: rgba(255,255,255,0.92) !important; }
        .app-sidebar .nav-link-item:hover { background: rgba(255,255,255,0.15); color: #fff !important; }
        .app-sidebar .nav-link-item.active,
        .app-sidebar .nav-link-item.active:hover { background: rgba(0,0,0,0.28); color: #fff !important; }
        .app-sidebar .nav-link-item.active::before { background: #fff !important; }
        .app-sidebar .nav-group-label { color: rgba(255,255,255,0.75) !important; }
        .app-sidebar .sidebar-brand-text,
        .app-sidebar .sidebar-brand-text span { color: #fff !important; }
        .app-sidebar .sidebar-nav::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.25); }
      `;
      let styleEl = document.getElementById('tenant-theme');
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = 'tenant-theme';
        document.head.appendChild(styleEl);
      }
      styleEl.textContent = css;
    }
  }, [tenant]);

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileOpen(false);
    setStudentMoreOpen(false);
  }, [location.pathname]);

  // Collapse on small screens initially
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 991) setCollapsed(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const primaryColor = tenant?.branding?.primaryColor || '#6366f1';

  // â”€â”€ Demo mode: use effective user + demo tenant modules â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const effectiveUser = demoMode ? getEffectiveUser() : user;
  const effectiveRoleKey = String(effectiveUser?.roleKey || effectiveUser?.role || '').trim().toLowerCase();
  const isStudentShell = effectiveRoleKey === 'student'
    && (demoMode || isCollegeStudentExperience(effectiveUser));
  const showCollegeSkillShareBrand = effectiveRoleKey === 'student'
    && isCollegeStudentExperience(effectiveUser);
  const studentCollegeBrand = showCollegeSkillShareBrand
    ? (effectiveUser?.studentExperience?.college || {})
    : {};
  const studentCollegeLogo = normalizeTenantAssetUrl(studentCollegeBrand.logoUrl || '');
  const studentCollegeName = String(
    studentCollegeBrand.name
    || effectiveUser?.collegeAccess?.partnerCollegeName
    || ''
  ).trim();

  useEffect(() => {
    if (effectiveRoleKey !== 'student' || demoMode) {
      setStudentHasAssessmentContent(false);
      return undefined;
    }

    let alive = true;
    const recordsFrom = (payload) => {
      const records = payload?.records ?? payload?.data?.records ?? payload?.data ?? payload;
      return Array.isArray(records) ? records : [];
    };

    Promise.allSettled([
      getData('/training/assessments/my'),
      getData('/quiz/scholarship/available'),
    ]).then((results) => {
      if (!alive) return;
      const hasAssignedAssessment = results[0].status === 'fulfilled'
        && recordsFrom(results[0].value).length > 0;
      const hasScholarshipQuiz = results[1].status === 'fulfilled'
        && recordsFrom(results[1].value).length > 0;
      setStudentHasAssessmentContent(hasAssignedAssessment || hasScholarshipQuiz);
    });

    return () => { alive = false; };
  }, [demoMode, effectiveRoleKey, effectiveUser?._id, effectiveUser?.id]);

  // Module pool: in demo mode always use demo tenant's modules (SA has no tenant modules);
  // for real tenant users, use their activeModules from AuthContext
  const modulePool = demoMode ? demoTenantModules : activeModules;

  // menuAccess: in demo mode, prefer the DB role from the demo tenant (industry-specific),
  // fall back to hardcoded DEMO_ROLE_MENUS only if DB has no menuAccess for that role key
  const demoRoleFromDB = demoMode
    ? demoTenantRoles.find(r => r.key === effectiveRoleKey)
    : null;

  const effectiveMenuAccess = demoMode
    ? (demoRoleFromDB?.menuAccess?.length > 0
        ? (effectiveRoleKey === 'team_lead' ? [...new Set([...demoRoleFromDB.menuAccess, 'enquiries'])] : demoRoleFromDB.menuAccess)
        : (DEMO_ROLE_MENUS.hasOwnProperty(effectiveRoleKey) ? DEMO_ROLE_MENUS[effectiveRoleKey] : null))
    : null; // AuthContext modules already include the server's resolved role access.

  // â”€â”€ Build sidebar nav â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  // Users / Roles & Permissions / Settings are administrative surfaces and
  // must ONLY appear for super_admin / admin / manager. Anything below that
  // (team_lead, faculty, teacher, student, parent, staff, viewer, etc.) MUST
  // NOT see them in the sidebar â€” even if their role's menuAccess somehow
  // contains those keys. This is enforced here defensively in addition to
  // the backend's own strip in /rbac/my-access.
  const ADMIN_ONLY_KEYS  = new Set(['users', 'roles', 'settings', 'app-settings', 'student-showcase']);
  const LEGACY_PAID_STUDENT_HIDDEN_KEYS = new Set(['attendance', 'edu_attendance', 'online_learning']);
  const ELEVATED_ROLES   = new Set(['super_admin', 'admin', 'manager']);
  const isElevated       = ELEVATED_ROLES.has(effectiveRoleKey);
  const isLegacyPaidStudent = effectiveRoleKey === 'student' && !isStudentShell;
  const LMS_VISIBLE_ROLES = new Set(['super_admin', 'admin', 'manager', 'team_lead', 'student']);
  const tenantIndustrySlug = String(
    tenant?.industry?.slug
    || localStorage.getItem('tenant_industry_slug')
    || ''
  ).toLowerCase();
  const shouldInjectLmsModule = ['training_institute', 'training', 'education'].includes(tenantIndustrySlug)
    && LMS_VISIBLE_ROLES.has(effectiveRoleKey)
    && !isLegacyPaidStudent;
  const cachedBranding = (() => {
    try {
      return JSON.parse(localStorage.getItem('tenant_branding') || '{}');
    } catch (_) {
      return {};
    }
  })();
  const tenantLogo = normalizeTenantAssetUrl(
    tenant?.branding?.logo
    || tenant?.branding?.logoUrl
    || tenant?.logo
    || tenant?.logoUrl
    || settingsLogo
    || cachedBranding?.logo
    || cachedBranding?.logoUrl
    || ''
  );
  const tenantHeaderLogo = tenantLogo || `${window.location.origin}/favicon.png`;
  const useStudentBottomNav = effectiveRoleKey === 'student' && isMobileViewport;

  const baseCoreNav = [
    { key: 'dashboard',        name: 'Dashboard',            icon: 'bi-speedometer2',  routePath: '/dashboard',        category: 'administration' },
    { key: 'users',            name: 'Users',                icon: 'bi-people',         routePath: '/users',            category: 'administration' },
    { key: 'roles',            name: 'Roles & Permissions',  icon: 'bi-shield-lock',    routePath: '/roles',            category: 'administration' },
    { key: 'student-showcase', name: 'Student Showcase',     icon: 'bi-megaphone',      routePath: '/student-showcase', category: 'administration' },
    { key: 'settings',         name: 'Settings',             icon: 'bi-gear',           routePath: '/settings',         category: 'administration' },
  ];
  // Strip admin surfaces for non-elevated roles BEFORE building allModules so
  // the "always include coreNav" branch below can't smuggle them back in.
  const coreNav = baseCoreNav.filter(c => !ADMIN_ONLY_KEYS.has(c.key) || isElevated);

  const allModules = [...coreNav];
  (modulePool || []).forEach(mod => {
    if (ADMIN_ONLY_KEYS.has(mod.key) && !isElevated) return; // never expose admin modules to non-elevated roles
    if (!coreNav.some(c => c.key === mod.key)) allModules.push(mod);
  });
  if (shouldInjectLmsModule && !allModules.some((mod) => mod.key === 'online_learning')) {
    allModules.push({
      key: 'online_learning',
      name: 'Online Learning / LMS',
      icon: 'bi-play-circle',
      routePath: '/lms',
      category: 'learning',
    });
  }

  // Apply role menuAccess filter â€” null means show all (only happens for
  // super_admin / admin or roles configured with no menuAccess restriction).
  // For elevated roles, coreNav is always visible. For non-elevated roles,
  // coreNav has already had admin keys stripped above, so the bypass here is
  // safe.
  const studentAssessmentModule = {
    key: 'assessments',
    name: 'Assessments',
    mobileLabel: 'Assess',
    icon: 'bi-clipboard-check',
    routePath: '/assessments',
    category: 'learning',
  };
  const collegeStudentModules = [
    { key: 'dashboard', name: 'Dashboard', icon: 'bi-house-door', routePath: '/lms?tab=dashboard', category: 'learning' },
    ...(studentHasAssessmentContent ? [studentAssessmentModule] : []),
    { key: 'lms-progress', name: 'Progress', icon: 'bi-bar-chart-line', routePath: '/lms?tab=progress', category: 'learning' },
    { key: 'lms-for-you', name: 'Explore', icon: 'bi-compass', routePath: '/lms?tab=for-you', category: 'learning' },
    { key: 'student-jobs', name: 'Jobs', icon: 'bi-briefcase', routePath: '/job-opportunities', category: 'learning' },
    { key: 'resumes', name: 'Resume', icon: 'bi-file-person', routePath: '/resumes', category: 'learning' },
  ];
  const paidStudentModules = [
    { key: 'dashboard', name: 'Dashboard', mobileLabel: 'Dashboard', icon: 'bi-speedometer2', routePath: '/dashboard', category: 'learning' },
    { key: 'assessments', name: 'Assessments', mobileLabel: 'Assessments', icon: 'bi-clipboard-check', routePath: '/assessments', category: 'learning' },
    { key: 'course-content', name: 'Course Content', mobileLabel: 'Course', icon: 'bi-book-half', routePath: '/course-content', category: 'learning' },
    { key: 'student_performance', name: 'Progress', mobileLabel: 'Progress', icon: 'bi-graph-up-arrow', routePath: '/performance', category: 'learning' },
    { key: 'feedback', name: 'Feedback', mobileLabel: 'Feedback', icon: 'bi-chat-square-heart', routePath: '/feedback', category: 'learning' },
  ];
  const visibleModulesUnsafe = isStudentShell
    ? collegeStudentModules
    : isLegacyPaidStudent
    ? paidStudentModules
    : effectiveMenuAccess
    ? allModules.filter((m) => (
        coreNav.some(c => c.key === m.key)
        || effectiveMenuAccess.includes(m.key)
        || (m.key === 'online_learning' && LMS_VISIBLE_ROLES.has(effectiveRoleKey))
      ))
    : allModules;
  // Final defense-in-depth strip: even if everything else fails, admin-only
  // modules must not be visible to non-elevated roles.
  const visibleModules = visibleModulesUnsafe
    .filter(m => !ADMIN_ONLY_KEYS.has(m.key) || isElevated)
    .filter(m => !isLegacyPaidStudent || !LEGACY_PAID_STUDENT_HIDDEN_KEYS.has(m.key))
    .filter(m => effectiveRoleKey !== 'student' || !['tasks', 'timetable'].includes(m.key));
  const collegeStudentBottomNavModules = [
    {
      key: 'dashboard',
      name: 'Dashboard',
      mobileLabel: 'Dashboard',
      icon: 'bi-house-door',
      routePath: '/lms?tab=dashboard',
      category: 'learning',
    },
    {
      key: 'lms-progress',
      name: 'Progress',
      mobileLabel: 'Progress',
      icon: 'bi-bar-chart-line',
      routePath: '/lms?tab=progress',
      category: 'learning',
    },
    ...(studentHasAssessmentContent ? [studentAssessmentModule] : []),
    {
      key: 'lms-for-you',
      name: 'Explore',
      mobileLabel: 'Explore',
      icon: 'bi-compass',
      routePath: '/lms?tab=for-you',
      category: 'learning',
    },
    {
      key: 'student-jobs',
      name: 'Jobs',
      mobileLabel: 'Jobs',
      icon: 'bi-briefcase',
      routePath: '/job-opportunities',
      category: 'learning',
    },
    {
      key: 'resumes',
      name: 'Resume',
      mobileLabel: 'Resume',
      icon: 'bi-file-person',
      routePath: '/resumes',
      category: 'learning',
    },
  ];
  const studentBottomNavModules = isLegacyPaidStudent
    ? paidStudentModules
    : collegeStudentBottomNavModules;


  const grouped = {};
  visibleModules.forEach(mod => {
    const cat = mod.category || 'operations';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(mod);
  });

  const initials = `${effectiveUser?.firstName?.[0] || user?.firstName?.[0] || ''}${effectiveUser?.lastName?.[0] || user?.lastName?.[0] || ''}`.toUpperCase();
  const currentQueryTab = new URLSearchParams(location.search).get('tab') || '';
  const isModuleActive = (mod) => {
    if (mod.key === 'student-more') {
      return ['/resumes'].includes(location.pathname);
    }
    const routePath = String(mod.routePath || `/${mod.key}`);
    if (routePath.startsWith('/lms?tab=')) {
      const targetTab = routePath.split('tab=')[1] || '';
      if (location.pathname !== '/lms') return false;
      return (currentQueryTab || 'dashboard') === targetTab;
    }
    return location.pathname === routePath || location.pathname.startsWith(`${routePath}/`);
  };
  return (
    <div className={`app-wrapper tenant-themed ${isStudentShell ? 'student-typography-shell' : ''} ${useStudentBottomNav ? 'student-bottom-nav-shell' : ''}`}>
      <style>{`
        @media (max-width: 991px) {
          .student-bottom-nav-shell {
            width: 100%;
            max-width: 100vw;
            overflow-x: hidden;
            overflow-x: clip;
          }
          .student-bottom-nav-shell .app-sidebar {
            display: none !important;
          }
          .student-bottom-nav-shell .app-main {
            margin-left: 0 !important;
            width: 100%;
            min-width: 0;
            max-width: 100vw;
            overflow-x: hidden;
            overflow-x: clip;
          }
          .student-bottom-nav-shell .app-content {
            width: 100%;
            min-width: 0;
            max-width: 100vw;
            padding-bottom: calc(54px + env(safe-area-inset-bottom, 0px)) !important;
            overflow-x: hidden;
            overflow-x: clip;
          }
          .student-bottom-nav-shell .app-content > * {
            min-width: 0;
            max-width: 100%;
          }
          .student-bottom-nav-shell .app-topbar {
            width: 100%;
            min-width: 0;
            background: rgba(255,255,255,0.98) !important;
            border-bottom: 1px solid #eef2ff;
          }
        }
      `}</style>
      {/* Mobile overlay */}
      {mobileOpen && !useStudentBottomNav && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1039, backdropFilter: 'blur(2px)' }}
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`app-sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''} ${isStudentShell ? 'student-shell' : ''}`}>
        {/* Brand */}
          <NavLink to={isStudentShell ? '/lms?tab=dashboard' : '/dashboard'} className="sidebar-brand" style={{ textDecoration: 'none' }}>
          {tenant?.branding?.logo
            ? <img src={tenant.branding.logo} alt="logo" className="sidebar-brand-logo" />
            : <div className="sidebar-brand-icon" style={{ background: primaryColor }}>
                <i className="bi bi-buildings-fill"></i>
              </div>
          }
          {!(isStudentShell && collapsed) && <div className={`sidebar-brand-text ${showCollegeSkillShareBrand ? 'college-skillshare-sidebar-brand' : ''}`.trim()}>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {showCollegeSkillShareBrand ? <span className="skillshare-wordmark">Skill Share</span> : (demoMode && !tenant ? (demoTenantName || 'Demo Tenant') : (tenant?.name || 'Organization'))}
            </div>
            <span style={demoMode ? { color: '#fbbf24' } : undefined}>
              {showCollegeSkillShareBrand
                ? 'by Top Skilled Academy'
                : demoMode
                ? `Demo: ${effectiveRoleKey}`
                /* Show the role's display name (e.g. "Faculty") rather than
                   the raw key (e.g. "teacher") â€” the legacy "Trainer" role
                   was renamed to Faculty in the migration but kept its
                   `key='teacher'` for back-compat, so reading roleKey here
                   surfaces the wrong label. role.name comes from /auth/login
                   and reflects the current display name. */
                : (role?.name || user?.roleKey || 'Member')}
            </span>
          </div>}
        </NavLink>

        {/* Nav */}
        <nav className="sidebar-nav">
          {CATEGORY_ORDER.filter(cat => grouped[cat]).map(cat => (
            <div key={cat}>
              {!isStudentShell && (
                <div
                  className="nav-group-label"
                  style={{
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.18) 0%, rgba(129, 140, 248, 0.12) 100%)',
                    color: '#dbe4ff',
                    border: '1px solid rgba(199, 210, 254, 0.32)',
                    borderRadius: 10,
                    padding: collapsed ? '6px 8px' : '8px 12px',
                    margin: collapsed ? '14px 8px 8px' : '14px 10px 8px',
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    lineHeight: 1.2,
                    textAlign: collapsed ? 'center' : 'left',
                    textTransform: 'uppercase',
                    boxShadow: '0 6px 16px rgba(79, 70, 229, 0.12), 0 1px 2px rgba(15, 23, 42, 0.08), inset 0 1px 0 rgba(255,255,255,0.08)',
                  }}
                >
                  {collapsed ? (CATEGORY_LABELS[cat] || cat).slice(0, 3) : (CATEGORY_LABELS[cat] || cat)}
                </div>
              )}
              {grouped[cat].map(mod => (
                <div className="nav-item" key={mod.key}>
                  <NavLink
                    to={mod.routePath || `/${mod.key}`}
                    className={() => `nav-link-item ${isModuleActive(mod) ? 'active' : ''}`}
                    title={collapsed
                      ? `${mod.name}${mod.key === 'users' && pendingUsersCount > 0 ? ` (${pendingUsersCount} pending)` : ''}`
                      : undefined}
                  >
                    <i className={normalizeBootstrapIcon(mod.icon)}></i>
                    {!(isStudentShell && collapsed) && <span className="nav-label">{mod.name}</span>}
                    {mod.key === 'users' && pendingUsersCount > 0 && (
                      <span
                        className="badge bg-warning text-dark ms-auto"
                        style={{ fontSize: 10, fontWeight: 700 }}
                        title={`${pendingUsersCount} pending approval${pendingUsersCount !== 1 ? 's' : ''}`}
                      >
                        {pendingUsersCount}
                      </span>
                    )}
                  </NavLink>
                </div>
              ))}
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="sidebar-footer">
          <NavLink to="/profile" className="nav-link-item" style={{ textDecoration: 'none', color: '#94a3b8' }} title={collapsed ? 'Profile' : undefined}>
            <div style={{ width: 20, height: 20, borderRadius: '50%', background: primaryColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#fff', fontWeight: 700, flexShrink: 0 }}>
              {initials}
            </div>
            {!(isStudentShell && collapsed) && <span className="nav-label" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {demoMode
                ? `${effectiveUser?.firstName || 'Demo'} ${effectiveUser?.lastName || ''}`
                : `${user?.firstName || ''} ${user?.lastName || ''}`}
            </span>}
          </NavLink>
          <button
            className="nav-link-item w-100 mt-1"
            onClick={() => setSignOutOpen(true)}
            title={collapsed ? 'Sign Out' : undefined}
            style={{ border: 'none', background: 'none', color: '#94a3b8', cursor: 'pointer' }}
          >
            <i className="bi bi-box-arrow-left"></i>
            {!(isStudentShell && collapsed) && <span className="nav-label">Sign Out</span>}
          </button>
          <VersionBadge collapsed={collapsed} />
        </div>
      </aside>

      {/* Main */}
      <div className={`app-main ${collapsed ? 'sidebar-collapsed' : ''}`}>
        {/* Topbar */}
        <header className={`app-topbar ${showCollegeSkillShareBrand ? 'college-branded-topbar' : ''}`.trim()}>
          {/* Desktop collapse toggle */}
          <button className="topbar-toggle d-none d-lg-flex" onClick={() => setCollapsed(c => !c)} title="Toggle sidebar">
            <i className={`bi ${collapsed ? 'bi-layout-sidebar-inset' : 'bi-layout-sidebar'}`}></i>
          </button>
          {/* Mobile open toggle */}
          {!useStudentBottomNav && (
            <button className="topbar-toggle d-lg-none" onClick={() => setMobileOpen(o => !o)} title="Open menu">
              <i className="bi bi-list" style={{ fontSize: 22 }}></i>
            </button>
          )}

          <div className="topbar-title">
            {isStudentShell ? (
              <div className={`student-topbar-brand ${showCollegeSkillShareBrand ? 'is-skillshare' : ''}`}>
                <img
                  src={showCollegeSkillShareBrand ? `${window.location.origin}/favicon.png` : tenantHeaderLogo}
                  alt={showCollegeSkillShareBrand ? 'SkillShare by Top Skilled Academy' : (tenant?.name || tenant?.companyName || 'Tenant logo')}
                />
                {showCollegeSkillShareBrand ? (
                  <>
                    <span className="student-skillshare-lockup">
                      <strong className="skillshare-wordmark">Skill Share</strong>
                      <small>by Top Skilled Academy</small>
                    </span>
                    {studentCollegeName && (
                      <span className="student-college-powered-lockup" title={studentCollegeName}>
                        {studentCollegeLogo ? (
                          <img src={studentCollegeLogo} alt="" />
                        ) : (
                          <span className="student-college-powered-fallback"><i className="bi bi-mortarboard-fill" /></span>
                        )}
                        <span className="student-college-powered-copy">
                          <small>Powered by</small>
                          <strong>{studentCollegeName}</strong>
                        </span>
                      </span>
                    )}
                  </>
                ) : (
                  <span className="student-default-brand-name">
                    {tenant?.name || tenant?.companyName || 'Top Skilled Academy'}
                  </span>
                )}
              </div>
            ) : (
              tenant?.name || tenant?.companyName || 'Dashboard'
            )}
          </div>

          <div className="topbar-actions">
            {/* Test notification bell â€” lets any user verify the popup+sound pipeline */}
            <button
              className="topbar-btn"
              title="Send test notification (verify popup & sound)"
              onClick={() => postData('/shared/notifications/ping').catch(() => {})}
              style={{ border: 'none', background: 'none', color: '#94a3b8', cursor: 'pointer', position: 'relative' }}
            >
              <i className="bi bi-bell-fill" style={{ fontSize: 14, opacity: 0.5 }}></i>
              <span style={{ position: 'absolute', top: 2, right: 2, width: 7, height: 7, borderRadius: '50%', background: '#22c55e', border: '1.5px solid #fff' }}></span>
            </button>
            <NavLink to="/notifications" className="topbar-btn" style={{ textDecoration: 'none', color: '#94a3b8', position: 'relative' }}
                     title={unreadNotifications > 0 ? `${unreadNotifications} unread notification${unreadNotifications === 1 ? '' : 's'}` : 'Notifications'}>
              <i className="bi bi-bell"></i>
              {unreadNotifications > 0 && (
                <span style={{
                  position: 'absolute',
                  top: 2, right: 2,
                  background: '#ef4444',
                  color: '#fff',
                  borderRadius: 10,
                  minWidth: 18,
                  height: 18,
                  fontSize: 10,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 5px',
                  lineHeight: 1,
                }}>
                  {unreadNotifications > 99 ? '99+' : unreadNotifications}
                </span>
              )}
            </NavLink>
            {/* Subscription is a billing surface â€” only Admin (or super_admin)
                should see it in the menu. Manager/team_lead/faculty/student/etc.
                don't need plan management visibility. */}
            {(effectiveRoleKey === 'admin' || user?.userType === 'super_admin') && (
              <NavLink to="/subscription" className="topbar-btn d-none d-md-flex" style={{ textDecoration: 'none', color: '#94a3b8' }} title="Subscription">
                <i className="bi bi-credit-card-2-front"></i>
              </NavLink>
            )}
            <NavLink
              to={isStudentShell ? '/lms?tab=my-subscription' : '/profile'}
              className="user-avatar"
              style={{ textDecoration: 'none' }}
              title={isStudentShell ? 'Subscription' : 'Profile'}
            >
              {initials}
            </NavLink>
          </div>
        </header>

        {/* Demo mode banner */}
        {demoMode && (
          <div style={{ background: '#fef3c7', borderBottom: '1px solid #fde68a', padding: '6px 20px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="bi bi-eye-fill text-warning"></i>
            <span>
              <strong>Demo Preview</strong> â€” viewing as <strong style={{ color: '#92400e' }}>{effectiveRoleKey}</strong>
              {demoTenantName && <span className="text-muted"> Â· {demoTenantName}</span>}
              <span className="text-muted"> Â· {visibleModules.length} modules visible</span>
            </span>
          </div>
        )}

        {/* Content */}
        <main className="app-content">
          <Outlet />
        </main>
      </div>

      {useStudentBottomNav && (
        <>
        {studentMoreOpen && (
          <div
            className="d-lg-none position-fixed"
            style={{ inset: 0, zIndex: 1044, background: 'rgba(15,23,42,0.16)' }}
            onClick={() => setStudentMoreOpen(false)}
          />
        )}
        {studentMoreOpen && (
          <div
            className="d-lg-none position-fixed"
            style={{
              right: 12,
              bottom: '70px',
              zIndex: 1046,
              width: 190,
              borderRadius: 20,
              background: 'rgba(255,255,255,0.98)',
              border: '1px solid rgba(226,232,240,0.95)',
              boxShadow: '0 18px 36px rgba(15,23,42,0.14)',
              padding: 8,
              backdropFilter: 'blur(18px)',
            }}
          >
            {[
              { key: 'resume', label: 'Resume', icon: 'bi-file-person', to: '/resumes' },
              { key: 'progress', label: 'Progress', icon: 'bi-bar-chart-line', to: '/lms?tab=progress' },
            ].map((item) => (
              <NavLink
                key={item.key}
                to={item.to}
                className="d-flex align-items-center gap-3 text-decoration-none"
                style={{
                  color: '#111827',
                  borderRadius: 14,
                  padding: '12px 14px',
                }}
                onClick={() => setStudentMoreOpen(false)}
              >
                <span
                  className="d-inline-flex align-items-center justify-content-center"
                  style={{ width: 34, height: 34, borderRadius: 12, background: 'rgba(99,102,241,0.10)', color: primaryColor }}
                >
                  <i className={normalizeBootstrapIcon(item.icon)} style={{ fontSize: 16, lineHeight: 1 }}></i>
                </span>
                <span style={{ fontSize: 14, fontWeight: 600 }}>{item.label}</span>
              </NavLink>
            ))}
          </div>
        )}
        <nav
          className="d-lg-none student-mobile-bottom-nav"
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1045,
            padding: '2px 6px calc(3px + env(safe-area-inset-bottom, 0px))',
            background: 'rgba(255,255,255,0.98)',
            backdropFilter: 'blur(18px)',
            borderTop: '1px solid rgba(226,232,240,0.9)',
            boxShadow: '0 -6px 18px rgba(15,23,42,0.06)',
          }}
        >
          <div
            className="student-mobile-bottom-nav-inner"
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${Math.max(studentBottomNavModules.length, 1)}, minmax(42px, 1fr))`,
              gap: 2,
              alignItems: 'stretch',
              border: 0,
              borderRadius: 0,
              background: 'transparent',
              padding: 0,
              boxShadow: 'none',
            }}
          >
            {studentBottomNavModules.map((mod) => {
              const active = isModuleActive(mod);
              if (mod.key === 'student-more') {
                return (
                  <button
                    key={mod.key}
                    type="button"
                    aria-label={mod.name}
                    title={mod.name}
                    onClick={() => setStudentMoreOpen((prev) => !prev)}
                    style={{
                      textDecoration: 'none',
                      color: active || studentMoreOpen ? primaryColor : '#64748b',
                      background: active || studentMoreOpen ? 'rgba(99,102,241,0.10)' : 'transparent',
                      border: active || studentMoreOpen ? `1px solid ${primaryColor}22` : '1px solid transparent',
                      borderRadius: 8,
                      minWidth: 0,
                      minHeight: 42,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0,
                      padding: '2px',
                    }}
                    className="border-0"
                  >
                    <span
                      className="d-inline-flex align-items-center justify-content-center"
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        background: active || studentMoreOpen ? 'linear-gradient(135deg, rgba(37,99,235,0.14) 0%, rgba(124,58,237,0.18) 100%)' : 'transparent',
                      }}
                    >
                      <i className={normalizeBootstrapIcon(mod.icon || 'bi-grid')} style={{ fontSize: 15, lineHeight: 1 }}></i>
                    </span>
                    <span style={{ width: '100%', overflow: 'hidden', textAlign: 'center', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 9, fontWeight: active || studentMoreOpen ? 700 : 500, lineHeight: 1 }}>
                      {mod.mobileLabel || mod.name}
                    </span>
                  </button>
                );
              }
              return (
                <NavLink
                  key={mod.key}
                  to={mod.routePath || `/${mod.key}`}
                  aria-label={mod.name}
                  title={mod.name}
                  style={{
                    textDecoration: 'none',
                    color: active ? primaryColor : '#64748b',
                    background: active ? 'rgba(99,102,241,0.10)' : 'transparent',
                    border: active ? `1px solid ${primaryColor}22` : '1px solid transparent',
                    borderRadius: 8,
                    minWidth: 0,
                    minHeight: 42,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0,
                    padding: '2px',
                  }}
                >
                  <span
                    className="d-inline-flex align-items-center justify-content-center"
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      background: active ? 'linear-gradient(135deg, rgba(37,99,235,0.14) 0%, rgba(124,58,237,0.18) 100%)' : 'transparent',
                    }}
                  >
                    <i className={normalizeBootstrapIcon(mod.icon)} style={{ fontSize: 15, lineHeight: 1 }}></i>
                  </span>
                  <span style={{ width: '100%', overflow: 'hidden', textAlign: 'center', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 9, fontWeight: active ? 700 : 500, lineHeight: 1 }}>
                    {mod.mobileLabel || mod.name}
                  </span>
                </NavLink>
              );
            })}
          </div>
        </nav>
        </>
      )}

      {/* Sign Out confirmation â€” styled modal instead of window.confirm so
          the popup matches the rest of the app and isn't a jarring native
          alert. Renders only while open, so there's no extra DOM otherwise. */}
      {/* Live notification toast â€” appears top-right when SSE delivers a new notification */}
      {liveToast && (
        <div
          style={{
            position: 'fixed', top: 20, right: 20, zIndex: 9999,
            maxWidth: 360, width: 'calc(100vw - 40px)',
            background: '#1e293b', color: '#f8fafc',
            borderRadius: 14, boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            padding: '14px 14px 14px 16px',
            display: 'flex', alignItems: 'flex-start', gap: 12,
            animation: 'toastSlideIn 0.3s cubic-bezier(0.34,1.56,0.64,1)',
          }}
        >
          <style>{`
            @keyframes toastSlideIn {
              from { opacity: 0; transform: translateX(80px) scale(0.92); }
              to   { opacity: 1; transform: translateX(0)    scale(1);    }
            }
          `}</style>
          <div style={{
            width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
            background: liveToast.type === 'official_activity_approval'
              ? 'rgba(59,130,246,0.25)'
              : liveToast.type === 'class_checkin_required'
              ? 'rgba(245,158,11,0.25)'
              : liveToast.type === 'lunch_overtime'
              ? 'rgba(234,88,12,0.25)'
              : (liveToast.type === 'shift_end_reminder' || liveToast.type === 'auto_clock_out')
              ? 'rgba(239,68,68,0.20)' : 'rgba(99,102,241,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
          }}>
            {liveToast.type === 'official_activity_approval' ? 'ðŸ“‹'
              : liveToast.type === 'class_checkin_required' ? 'âš ï¸'
              : liveToast.type === 'lunch_overtime' ? 'ðŸ½ï¸'
              : liveToast.type === 'shift_end_reminder' ? 'ðŸ•'
              : liveToast.type === 'auto_clock_out' ? 'ðŸ”´'
              : 'ðŸ””'}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 3, color: '#f8fafc' }}>
              {liveToast.title || 'Notification'}
            </div>
            <div style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.45 }}>
              {liveToast.message}
            </div>
            {liveToast.link && (
              <a
                href={liveToast.link}
                onClick={() => { setLiveToast(null); if (toastTimerRef.current) clearTimeout(toastTimerRef.current); }}
                style={{ fontSize: 12, color: '#818cf8', marginTop: 6, display: 'inline-block', textDecoration: 'none' }}
              >
                View â†’
              </a>
            )}
          </div>
          <button
            onClick={() => { setLiveToast(null); if (toastTimerRef.current) clearTimeout(toastTimerRef.current); }}
            style={{
              background: 'none', border: 'none', color: '#64748b', cursor: 'pointer',
              padding: '0 2px', fontSize: 20, lineHeight: 1, flexShrink: 0, marginTop: -2,
            }}
            aria-label="Dismiss"
          >Ã—</button>
        </div>
      )}

      {signOutOpen && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setSignOutOpen(false); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 1080,
            background: 'rgba(15, 23, 42, 0.55)',
            backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
            animation: 'fadeIn 0.18s ease',
          }}
        >
          <style>{`
            @keyframes fadeIn   { from { opacity: 0 } to { opacity: 1 } }
            @keyframes popIn    { from { opacity: 0; transform: scale(0.92) translateY(8px) } to { opacity: 1; transform: scale(1) translateY(0) } }
          `}</style>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="signout-title"
            style={{
              width: '100%', maxWidth: 400,
              background: '#fff',
              borderRadius: 16,
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(15, 23, 42, 0.35)',
              animation: 'popIn 0.22s cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            {/* Icon header â€” soft red ring around the sign-out icon */}
            <div style={{
              padding: '28px 24px 18px',
              textAlign: 'center',
              borderBottom: '1px solid #f1f5f9',
            }}>
              <div style={{
                width: 64, height: 64, borderRadius: '50%',
                background: 'rgba(239, 68, 68, 0.12)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 14px',
              }}>
                <i className="bi bi-box-arrow-left" style={{ fontSize: 28, color: '#dc2626' }}></i>
              </div>
              <h5 id="signout-title" style={{ margin: 0, fontWeight: 700, color: '#0f172a' }}>
                Sign out?
              </h5>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
                You'll be returned to the login page. Any unsaved changes
                in this tab may be lost.
              </p>
              {(user?.firstName || user?.lastName) && (
                <div style={{
                  marginTop: 14,
                  fontSize: 12, color: '#475569',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '4px 10px', borderRadius: 999,
                  background: '#f8fafc', border: '1px solid #e2e8f0',
                }}>
                  <i className="bi bi-person-circle"></i>
                  Signed in as <strong style={{ color: '#0f172a' }}>{user?.firstName} {user?.lastName}</strong>
                </div>
              )}
            </div>
            {/* Buttons */}
            <div style={{ padding: 16, display: 'flex', gap: 10 }}>
              <button
                type="button"
                onClick={() => setSignOutOpen(false)}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  fontSize: 14, fontWeight: 600,
                  background: '#fff', color: '#334155',
                  border: '1px solid #cbd5e1', borderRadius: 10,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { setSignOutOpen(false); logout(); }}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  fontSize: 14, fontWeight: 700,
                  background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                  color: '#fff',
                  border: 'none', borderRadius: 10,
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(220, 38, 38, 0.4)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)';   e.currentTarget.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.3)'; }}
              >
                <i className="bi bi-box-arrow-left me-2"></i>Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
